import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useT } from "@/lib/i18n";
import StoryWheel from "@/components/ux/StoryWheel";
import { storyShotsFor } from "@/data/storyMedia";
import { getStoredAdEventType } from "@/lib/quizEventType";
import type { QuizAudience } from "@/lib/quizAudience";
import { BEAT_TRANSITION } from "../beats";
import { BLEED } from "../layout";
import ShrinkingBeat from "../ShrinkingBeat";
import type { StatementScreenDef, ScreenComponentProps } from "../types";

/**
 * A handoff between acts. The design's choreography: beat one lands, then
 * shrinks up and dims as beat two opens under it. Question screens stay calm;
 * these carry the motion.
 *
 * Beats advance on tap (the whole screen is the target), and the last beat's
 * tap moves the flow on. Copy is fully interpolated, so a beat can say the
 * user's city back to them or summarize what they just picked.
 */

/**
 * Which story deck the wheel turns. Audience picks the pack; the ad's event
 * type decides which card leads, so a coffee ad opens on a cafe rather than a
 * dinner table. Shared with the intro's print conveyor via `storyShotsFor`,
 * so the two screens can't drift into showing different products.
 */
function wheelShots(audience: QuizAudience) {
  return storyShotsFor(audience, getStoredAdEventType());
}

// Room the reveal's copy needs below the wheel: headline, body, and the CTA.
const REVEAL_CHROME_PX = 430;

const PING_DURATION_S = 2.4;

function CityPulse({ label }: { label?: string }) {
  const reducedMotion = useReducedMotion();
  return (
    <div className="flex flex-col items-center">
      {/* The city said back in the design's kicker treatment — it names what
          the radar is scanning, which is the whole point of the beat. */}
      {label && (
        <div className="font-Poppins text-[10px] font-semibold tracking-[0.3em] uppercase text-primary-400">
          {label}
        </div>
      )}
      <div className="relative w-[190px] h-[190px] mt-5" aria-hidden>
      <div className="absolute inset-0 rounded-full border border-primary-500/25" />
      <div className="absolute inset-[34px] rounded-full border border-primary-500/40" />

      {/* ONE ping, as designed — a single ripple travelling out, then a beat
          of rest before the next. Three staggered rings covered the pause but
          read as hectic: something was always moving, so nothing landed.
          The rest is the rhythm.

          Scale, opacity and duration are the design's ping (.6 → 1.5,
          .8 → 0, 2.4s) on CSS's default `ease` curve. The one addition is the
          fade UP over the first tenth: the design cuts straight to .8 at
          scale .6, which is large enough to see blink back into existence
          every cycle. Rising from nothing keeps the loop point invisible
          without touching the pacing. */}
      <motion.div
        className="absolute inset-0 rounded-full border-[1.5px] border-primary-500"
        initial={{ scale: 0.6, opacity: 0 }}
        animate={
          reducedMotion
            ? { scale: 1, opacity: 0.35 }
            : { scale: [0.6, 0.69, 1.5], opacity: [0, 0.8, 0] }
        }
        transition={
          reducedMotion
            ? { duration: 0 }
            : {
                duration: PING_DURATION_S,
                times: [0, 0.1, 1],
                repeat: Infinity,
                ease: [0.25, 0.1, 0.25, 1],
              }
        }
      />

      <div
        className="absolute top-1/2 left-1/2 w-3.5 h-3.5 -mt-[7px] -ml-[7px] rounded-full bg-primary-500"
        style={{ boxShadow: "0 0 18px rgba(239,98,37,0.9)" }}
      />
      </div>
    </div>
  );
}

function FilmPrints() {
  return (
    <div className="relative w-[240px] h-[250px] mx-auto" aria-hidden>
      <img
        src="/img/quiz/quiz-community-male.webp"
        alt=""
        className="absolute top-3 -left-2 w-[145px] h-[175px] object-cover rounded-sm border-[6px] border-b-[22px] border-[#F6F2E9] opacity-90"
        style={{ transform: "rotate(-7deg)", filter: "sepia(.28) saturate(.82)" }}
      />
      <motion.img
        src="/img/quiz/quiz-community-mixed.webp"
        alt=""
        className="absolute top-0 -right-1 w-[155px] h-[188px] object-cover rounded-sm border-[6px] border-b-[24px] border-[#F6F2E9]"
        style={{ filter: "sepia(.26) saturate(.85)" }}
        initial={{ rotate: 4 }}
        animate={{ rotate: 4, y: [0, -8, 0] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}

export default function StatementScreen({
  def,
  rt,
}: ScreenComponentProps<StatementScreenDef>) {
  const { t } = useT();
  // Beat state lives in the runner so the shared dock button and a tap on the
  // screen body drive the same progression.
  const beatIndex = rt.beatIndex;
  const values = useMemo(() => def.values?.(rt.copy) ?? {}, [def, rt.copy]);
  // Built once per audience — every entry is an already-bundled asset URL, so
  // this is just array construction, but the wheel remounts if the identity
  // changes and that would restart every clip.
  const shots = useMemo(
    () => (def.visual === "wheel" ? wheelShots(rt.audience) : []),
    [def.visual, rt.audience],
  );

  // Beats already played stay mounted. That's the whole mechanic: beat one is
  // the same element throughout, animating its own type down to make room,
  // rather than being unmounted and replaced by a separate small line.
  const played = def.beats.slice(0, beatIndex + 1);
  // Only the radar handoff centres — the design leaves the others ranged left.
  const centered = def.visual === "pulse";

  return (
    <button
      type="button"
      // Always the next screen, never the next beat. The beats run themselves
      // on a timer, so a tap here can only mean the reader is done with this
      // screen — including when they are done before the copy is.
      onClick={rt.next}
      className="flex-1 flex flex-col text-left w-full"
      aria-label={t("onboarding.tapToContinue")}
    >
      <div
        className={`flex-1 flex flex-col justify-center ${
          centered ? "items-center text-center" : ""
        }`}
      >
        {def.visual === "pulse" && <CityPulse label={values.city} />}
        {def.visual === "prints" && <FilmPrints />}
        {def.visual === "wheel" && (
          <StoryWheel
            shots={shots}
            active
            chromeReservePx={REVEAL_CHROME_PX}
            className={BLEED}
          />
        )}

        <div className="flex flex-col">
          {played.map((b, i) => {
            const active = i === beatIndex;
            return (
              <motion.div
                key={b.key}
                // An opening beat grows its own box; it does not fade in at
                // full height. The distinction is the whole reason this reads
                // smoothly: `y` is a transform, so an entrance animated with
                // it occupies its full height from the first frame. This
                // stack is vertically centred, so that put half the new
                // beat's height into the layout instantly — everything above,
                // radar included, jumped up in one frame and then drifted
                // back down as the beat above shrank. Growing the height on
                // the same curve as that shrink keeps every layout change
                // continuous, and reveals the new line top-down, which is the
                // "opens under it" the design asks for.
                //
                // marginBottom is animated for the same reason: toggling
                // `mb-4` by class was a second instant 16px step, fired at
                // exactly the moment of the jump.
                initial={i === 0 ? false : { opacity: 0, height: 0 }}
                animate={{
                  opacity: 1,
                  height: "auto",
                  marginBottom: active ? 0 : 16,
                }}
                transition={BEAT_TRANSITION}
                style={{ overflow: "hidden" }}
                className={centered ? "w-full" : ""}
              >
                {/* Lead and body belong to their own beat only. They collapse
                    away rather than shrinking — resizing them would re-wrap
                    them for no benefit, since they are gone a moment later. */}
                {b.hasLead && (
                  <motion.p
                    initial={false}
                    animate={{
                      height: active ? "auto" : 0,
                      opacity: active ? 1 : 0,
                      marginBottom: active ? 12 : 0,
                    }}
                    transition={BEAT_TRANSITION}
                    className="font-display font-medium text-[16px] leading-snug text-neutral-500 dark:text-neutral-400 overflow-hidden"
                  >
                    {t(`${def.copyKey}.${b.key}.lead`, values)}
                  </motion.p>
                )}

                <ShrinkingBeat shrunk={!active} origin={centered ? "top center" : "top left"}>
                  <h2 className={`font-display font-semibold text-[34px] leading-[1.16] tracking-tight text-neutral-900 dark:text-neutral-50 max-w-[320px] ${centered ? "mx-auto" : ""}`}>
                    {t(`${def.copyKey}.${b.key}.headline`, values)}
                  </h2>
                </ShrinkingBeat>

                {b.hasBody && (
                  <motion.p
                    initial={false}
                    animate={{
                      height: active ? "auto" : 0,
                      opacity: active ? 1 : 0,
                      marginTop: active ? 16 : 0,
                    }}
                    transition={BEAT_TRANSITION}
                    className="text-[15px] leading-relaxed text-neutral-500 dark:text-neutral-400 max-w-[330px] overflow-hidden"
                  >
                    {t(`${def.copyKey}.${b.key}.body`, values)}
                  </motion.p>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

    </button>
  );
}
